import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Check, Star, Leaf, Plus, Minus,
  CalendarCheck, FileSignature, Radar, Sparkles, Users, ListChecks, KanbanSquare, Video,
} from "lucide-react";
import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinks } from "@/lib/i18n/locale";
import { BeforeAfterSlider } from "@/components/marketing/BeforeAfterSlider";
import { useCurrency } from "@/lib/ecom/currency";
import { formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import {
  getPlanMonthly,
  formatPlanPrice,
  defaultPlanPriceText,
} from "@/components/marketing/pricing-data";
import prodMetal from "@/assets/products/custom-black-metal.webp";
import prodGold from "@/assets/products/custom-gold.webp";
import prodBamboo from "@/assets/products/custom-bamboo.webp";
import logoSvg from "@/assets/banana-logo.svg";
import heroCard from "@/assets/hero-card-new.webp";
import networking from "@/assets/networking-new.png";
import aiAbstract from "/lovable-uploads/11749302-d433-4877-9acb-ac3108b72948.webp";
import phoneCard from "/lovable-uploads/38b31f4c-5833-4861-9072-f959be0b21bb.webp";
import nfcTrio from "@/assets/nfc-card-trio-v2.png";
import nfcTap from "@/assets/nfc-card-tap-new.png";
import nfcLifestyle from "@/assets/nfc-card-lifestyle-new.png";
import nfcThin from "@/assets/nfc-thin.webp";
import floatCardScan from "@/assets/float-card-scan.webp";
import floatProfile from "@/assets/float-profile.webp";
import floatData from "@/assets/float-data.png";

export const Route = createFileRoute("/vi/")({
  head: () => {
    const { canonical, alternates } = alternateLinks("/", "vi");
    return {
      meta: [
        { title: "Danh thiếp kỹ thuật số & Nền tảng kết nối AI | Banana Cards" },
        { name: "description", content: "Banana Cards là nền tảng kết nối được hỗ trợ bởi AI, kết hợp danh thiếp kỹ thuật số, chia sẻ NFC, thu thập khách hàng tiềm năng, CRM và quản lý liên hệ thông minh trong một nơi." },
        { property: "og:title", content: "Danh thiếp kỹ thuật số & Nền tảng kết nối AI | Banana Cards" },
        { property: "og:description", content: "Banana Cards là nền tảng kết nối được hỗ trợ bởi AI, kết hợp danh thiếp kỹ thuật số, chia sẻ NFC, thu thập khách hàng tiềm năng, CRM và quản lý liên hệ thông minh trong một nơi." },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "vi_VN" },
        { property: "og:locale:alternate", content: "en_US" },
      ],
      links: [canonical, ...alternates],
    };
  },
  component: IndexPage,
});

const LOGOS = ["Google", "Stripe", "HubSpot", "Salesforce", "Notion", "Linear", "Toyota", "Canva", "Finvi", "Coca-Cola"];

const PRODUCTS = [
  {
    title: "Thẻ Danh Thiếp NFC",
    desc: "Chạm vào bất kỳ điện thoại nào. Chia sẻ hồ sơ kỹ thuật số đầy đủ ngay lập tức — người nhận không cần cài ứng dụng. Kim loại, nhám hoặc tre.",
    img: nfcTrio,
    bullets: ["Chạm để chia sẻ, không cần ứng dụng", "Hoàn thiện kim loại, nhám và tre", "Logo & thiết kế tùy chỉnh"],
    href: "/shop",
    cta: "Mua thẻ NFC",
  },
  {
    title: "Thẻ Ảo",
    desc: "Hồ sơ kỹ thuật số hoàn toàn — chia sẻ qua mã QR, liên kết hoặc Apple/Google Wallet. Luôn cập nhật. Luôn bên bạn.",
    img: phoneCard,
    bullets: ["QR, liên kết, Apple & Google Wallet", "Cập nhật bất cứ lúc nào, ngay lập tức", "Không cần thẻ vật lý"],
    href: "/platform/cards",
    cta: "Tạo thẻ của bạn",
  },
  {
    title: "Ứng dụng Banana",
    desc: "Tích hợp đặt lịch, hợp đồng, đánh giá và công cụ AI vào hồ sơ. Một nền tảng, mọi quy trình.",
    img: aiAbstract,
    bullets: ["Đặt lịch, hợp đồng, đánh giá", "AI theo dõi & làm giàu dữ liệu", "50+ tích hợp gốc"],
    href: "/platform/bookings",
    cta: "Khám phá nền tảng",
  },
];

const CARD_VARIANTS: { slug: string; name: string; desc: string; img: string; href: string }[] = [
  { slug: "metal",     name: "Thẻ AI Kim Loại Tùy Chỉnh",  desc: "Thẻ NFC kim loại cao cấp bền bỉ — được hỗ trợ bởi AI.",        img: prodMetal,  href: "/shop/metal" },
  { slug: "24kt-gold", name: "Thẻ AI Vàng 24kt",     desc: "Thẻ NFC mạ vàng 24kt sang trọng — thiết kế để gây ấn tượng.",       img: prodGold,   href: "/shop/24kt-gold" },
  { slug: "bamboo",    name: "Thẻ AI Tre",        desc: "Thẻ NFC tre bền vững — thông minh, tự nhiên, khó quên.",  img: prodBamboo, href: "/shop/bamboo" },
];

const PLATFORM_FEATURES = [
  {
    eyebrow: "Một hồ sơ kỹ thuật số tuyệt đẹp.",
    italic: "Luôn sẵn sàng.",
    desc: "Thẻ của bạn nằm tại một liên kết vĩnh viễn — được thương hiệu hóa đầy đủ, có thể chia sẻ ngay lập tức và luôn hiển thị thông tin mới nhất. Không cần ứng dụng để xem hoặc lưu.",
    chips: ["Mã QR", "Apple Wallet", "Google Wallet", "Chạm NFC", "Chia sẻ liên kết"],
    img: phoneCard,
    href: "/platform/cards",
    cta: "Danh thiếp số",
  },
  {
    eyebrow: "Không bao giờ mất liên hệ",
    italic: "nữa.",
    desc: "Mọi người quét thẻ của bạn đều được ghi nhận tự động. Thêm ghi chú, thẻ và nhắc nhở theo dõi. Xem ai đã xem thẻ của bạn và khi nào.",
    chips: ["Tự động ghi nhận", "Ghi chú & Thẻ", "Theo dõi", "Phân tích"],
    img: networking,
    href: "/platform/contacts",
    cta: "Liên hệ",
  },
  {
    eyebrow: "Quản lý thẻ của đội nhóm",
    italic: "từ một nơi duy nhất.",
    desc: "Tạo thẻ cho toàn bộ đội nhóm trong vài phút. Đặt nguyên tắc thương hiệu, cập nhật thông tin liên hệ tập trung và theo dõi mức độ tương tác trên toàn tổ chức.",
    chips: ["Bảng điều khiển đội nhóm", "Cấp phát hàng loạt", "Kiểm soát thương hiệu", "Quản trị"],
    img: heroCard,
    href: "/teams",
    cta: "Đội nhóm",
  },
  {
    eyebrow: "Kết nối với các công cụ",
    italic: "bạn đã dùng.",
    desc: "Đẩy liên hệ mới trực tiếp vào CRM của bạn. Đồng bộ với HubSpot, Salesforce, Pipedrive và nhiều hơn — không nhập liệu thủ công.",
    chips: ["HubSpot", "Salesforce", "Outlook", "Zapier", "Pipedrive", "Slack"],
    img: aiAbstract,
    href: "/platform/integrations",
    cta: "Tích hợp",
  },
  {
    eyebrow: "Biết chính xác thẻ của bạn",
    italic: "hoạt động ra sao.",
    desc: "Theo dõi lượt xem, lưu và nhấp liên kết theo thời gian thực. Hiểu những liên hệ nào tương tác và tối ưu ROI kết nối của bạn.",
    chips: ["Lượt xem thẻ", "Lượt lưu", "Nhấp liên kết", "Phân tích đội nhóm"],
    img: nfcLifestyle,
    href: "/platform/analytics",
    cta: "Phân tích",
  },
];

const STEPS = [
  { tag: "Cài đặt",   title: "Tạo thẻ của bạn",  desc: "Tạo hồ sơ kỹ thuật số trong vài phút. Thêm ảnh, liên kết và thương hiệu. Tùy chỉnh thiết kế thẻ NFC nếu bạn đặt thẻ vật lý." },
  { tag: "Chia sẻ",   title: "Kết nối mọi nơi", desc: "Chạm thẻ NFC, hiển thị mã QR hoặc gửi liên kết. Người nhận thấy hồ sơ đầy đủ ngay lập tức — không ứng dụng, không rào cản." },
  { tag: "Quản lý",  title: "Mở rộng mạng lưới",desc: "Mọi kết nối được tự động lưu và tổ chức. Theo dõi thông minh hơn, đồng bộ với CRM và không bao giờ để khách hàng tiềm năng nguội lạnh." },
];

const PERSONAS = [
  { title: "Dành cho lãnh đạo & nhà sáng lập",      desc: "Khiến mỗi ấn tượng đầu tiên đều có giá trị. Thẻ của bạn phản ánh chất lượng thương hiệu — cao cấp, tinh tế, luôn cập nhật.", img: heroCard,     href: "/solutions/founders", cta: "Khám phá giải pháp cho nhà sáng lập" },
  { title: "Dành cho bán hàng & phát triển kinh doanh",    desc: "Chốt nhiều thương vụ hơn. Ghi nhận mọi khách hàng tiềm năng. Đồng bộ liên hệ vào CRM tự động và theo dõi trước đối thủ.",     img: networking,   href: "/solutions/sales",    cta: "Xem tính năng bán hàng" },
  { title: "Dành cho sáng tạo & freelancer", desc: "Nổi bật giữa đám đông. Thẻ số của bạn là một portfolio mini — thêm website, mạng xã hội, video và mẫu công việc.",          img: phoneCard,    href: "/solutions/founders", cta: "Xem tính năng freelancer" },
];

const SPECS = [
  { v: "0.8mm",       l: "mỏng",          d: "Mỏng hơn thẻ tín dụng tiêu chuẩn" },
  { v: "NFC + QR",    l: "hai trong một",    d: "Hai cách chia sẻ trong một thẻ" },
  { v: "Mọi điện thoại",   l: "tương thích", d: "iPhone & Android, không cần ứng dụng" },
  { v: "Tùy chỉnh",      l: "thiết kế",        d: "In tràn lề, khắc hoặc dập nổi" },
  { v: "Tức thì",     l: "cập nhật",       d: "Thay đổi hồ sơ kỹ thuật số bất cứ lúc nào" },
  { v: "Bền bỉ",     l: "chế tạo",         d: "Chống nước, chống trầy xước" },
];

const STATS = [
  { v: "200k+", l: "thẻ đã giao trên toàn cầu" },
  { v: "100+",  l: "quốc gia đã giao hàng" },
  { v: "4.9",   l: "đánh giá trung bình" },
  { v: "50k+",  l: "người dùng đang hoạt động" },
];

const REVIEWS = [
  { q: "banana.cards đã hoàn toàn thay đổi cách đội ngũ bán hàng của chúng tôi kết nối. Mọi khách hàng tiềm năng đều được ghi nhận tự động và đẩy vào Salesforce. Không cần thao tác thủ công.", a: "James T.", r: "Phó Chủ tịch Kinh doanh, Finvi" },
  { q: "Thẻ NFC rất đẹp — mọi người luôn hỏi về nó tại sự kiện. Khả năng cập nhật thông tin bất cứ lúc nào mà không cần in lại là một bước ngoặt.", a: "Sarah M.", r: "Nhà sáng lập" },
  { q: "Chúng tôi triển khai cho 200 nhân viên chỉ trong một buổi chiều. Bảng điều khiển đội nhóm giúp quản lý thẻ của mọi người tập trung cực kỳ dễ dàng.", a: "Mark R.",  r: "Giám đốc CNTT" },
  { q: "Tôi thích việc người nhận không cần tải gì cả. Họ chỉ cần chạm hoặc quét và bùm — liên hệ của tôi đã có trên điện thoại họ.",                       a: "Lucas P.", r: "Môi giới bất động sản" },
  { q: "Cuối cùng cũng có một danh thiếp cao cấp như thương hiệu của chúng tôi. Thẻ kim loại khắc logo trông tuyệt vời.",                                     a: "Elena K.", r: "Giám đốc Sáng tạo" },
  { q: "Tính năng phân tích thật tuyệt vời — tôi thấy chính xác ai đã xem thẻ sau mỗi sự kiện và theo dõi khi cuộc trò chuyện còn mới.",               a: "David W.", r: "Chuyên viên Kinh doanh" },
];

const REVIEWS_EXTRA = [
  { q: "Là một Quản lý Account Executive, công cụ này giúp tôi quản lý liên hệ cho bản thân, đội nhóm và hơn thế nữa. Chiếc thẻ đã cải thiện hiệu quả đáng kể.", a: "Anna-Rose H.", r: "Quản lý Account Executive" },
  { q: "Không còn mất danh thiếp từ người khác và hết thẻ để người khác làm mất. Tuyệt vời!", a: "Allen P.", r: "Huấn luyện viên Sự nghiệp" },
  { q: "Cứu lấy cây xanh và tính năng chạm rất tuyệt với điện thoại đời mới.", a: "Max R.", r: "Nhân viên bán hàng" },
  { q: "Tôi thực sự yêu thích sự đơn giản, lựa chọn chất liệu và thiết kế thanh lịch. Giao diện ứng dụng dùng rất thú vị.", a: "Angelica A.", r: "Đồng sở hữu" },
  { q: "Rất dễ dàng trao đổi thông tin với bất kỳ ai bạn gặp. Không cần danh thiếp in ấn. Thông tin vào thẳng danh bạ của bạn.", a: "Ozana G.", r: "Trưởng phòng Marketing" },
  { q: "Khả năng tùy chỉnh màu sắc, phông chữ và bố cục khiến nó phản ánh hoàn hảo thương hiệu hoặc phong cách của bạn.", a: "Tatiana E.", r: "Đại lý Du lịch" },
  { q: "Thiết kế và sự đơn giản khi thiết lập hồ sơ, cộng với sự tiện lợi khi chạm thẻ để chia sẻ mọi thứ — thật tuyệt vời.", a: "Brandon R.", r: "Thanh tra Công nghiệp" },
  { q: "Tôi cá nhân thích sự dễ dàng và bảo mật của việc chia sẻ thẻ số. Tôi không cần mang theo thẻ bổ sung.", a: "Priya N.", r: "Tư vấn viên" },
  { q: "Bền vững, đẹp và toàn bộ đội ngũ lãnh đạo của chúng tôi đều dùng. Nâng cấp lớn từ giấy.", a: "Diego F.", r: "COO" },
];

const FAQS = [
  { q: "Thẻ Banana NFC hoạt động như thế nào?", a: "Chỉ cần chạm thẻ vào bất kỳ smartphone hiện đại nào — iPhone hoặc Android. Hồ sơ kỹ thuật số đầy đủ của bạn mở ngay lập tức trong trình duyệt của người nhận. Không bên nào cần ứng dụng." },
  { q: "Tôi có cần ứng dụng để sử dụng không?", a: "Không. Người nhận thẻ không cần cài đặt gì. Là chủ thẻ, bạn quản lý hồ sơ từ bảng điều khiển web hoặc ứng dụng di động — tùy bạn chọn." },
  { q: "Tôi có thể cập nhật thông tin sau khi thẻ đã in không?", a: "Có — đó chính là điểm chính. Thẻ vật lý của bạn liên kết với hồ sơ số trực tiếp. Thay đổi công việc, số điện thoại, liên kết hoặc thiết kế bất cứ lúc nào và nó sẽ cập nhật ở mọi nơi ngay lập tức." },
  { q: "Nó có hoạt động với điện thoại của tôi không?", a: "Thẻ Banana hoạt động với hầu hết mọi smartphone sản xuất trong 7+ năm qua. iPhone XR trở lên mở bằng một cú chạm và mọi điện thoại đều có thể quét mã QR tích hợp." },
  { q: "Tôi có thể đặt thẻ cho toàn bộ đội nhóm không?", a: "Hoàn toàn được. Gói Teams cho phép bạn cấp phát thẻ hàng loạt, kiểm soát thương hiệu tập trung và quản lý hồ sơ của mọi nhân viên từ một bảng điều khiển quản trị." },
  { q: "Gói miễn phí bao gồm những gì?", a: "Một danh thiếp số đầy đủ với chia sẻ QR + liên kết, hỗ trợ Apple & Google Wallet, phân tích cơ bản và lưu liên hệ không giới hạn. Không cần thẻ tín dụng để bắt đầu." },
  { q: "Bạn có giao hàng quốc tế không?", a: "Có — chúng tôi giao đến 100+ quốc gia với theo dõi đơn hàng. Phí và thời gian giao hàng được hiển thị khi thanh toán dựa trên điểm đến." },
  { q: "Chính sách đổi trả là gì?", a: "Nếu bạn không hài lòng với thẻ, liên hệ chúng tôi trong vòng 30 ngày để được thay thế hoặc hoàn tiền miễn phí. Thẻ in tùy chỉnh được bảo hành cho lỗi sản xuất." },
];

const BLOGS = [
  { title: "Danh Thiếp Số Là Gì? (Hướng Dẫn Đầy Đủ 2026)", excerpt: "Khám phá danh thiếp số là gì, hoạt động ra sao và tại sao 88% chuyên gia thích chúng hơn giấy.", img: phoneCard, href: "/about" },
  { title: "8 Danh Thiếp Số Tốt Nhất 2026 (Xếp Hạng & Đánh Giá)", excerpt: "Chúng tôi đã đánh giá và thử nghiệm 8 nền tảng danh thiếp số tốt nhất — giá, gói miễn phí và tính năng.", img: networking, href: "/about" },
  { title: "Cách Tạo Danh Thiếp Số Trong 5 Phút", excerpt: "Tạo một danh thiếp số chuyên nghiệp trong 5 phút. Không cần ứng dụng, Apple Wallet miễn phí, hoạt động trên mọi thiết bị.", img: nfcTrio, href: "/about" },
  { title: "NFC vs QR: Phương Pháp Chia Sẻ Nào Thắng Năm 2026?", excerpt: "So sánh trực tiếp hai cách chia sẻ thông tin liên hệ phổ biến nhất — và lý do chúng tôi dùng cả hai.", img: nfcTap, href: "/about" },
  { title: "Lập Luận Bền Vững Cho Việc Không Dùng Giấy", excerpt: "Danh thiếp giấy tiêu tốn 7 triệu cây mỗi năm. Đây là cách thẻ số thay đổi điều đó.", img: aiAbstract, href: "/about" },
  { title: "Thiết Kế Thẻ Khiến Người Khác Nhớ Đến", excerpt: "Lựa chọn màu sắc, chất liệu và phông chữ giúp thẻ NFC vật lý của bạn khó quên tại sự kiện.", img: nfcLifestyle, href: "/about" },
];

const PLANS = [
  {
    name: "Miễn phí",
    planKey: "free" as const,
    desc: "Hoàn hảo cho cá nhân mới bắt đầu.",
    cta: "Bắt đầu miễn phí",
    href: "/signup",
    features: ["1 thẻ số", "Chia sẻ QR & liên kết", "Apple & Google Wallet", "Phân tích cơ bản", "Lưu liên hệ không giới hạn"],
  },
  {
    name: "Pro AI",
    planKey: "pro_ai" as const,
    desc: "Dành cho chuyên gia kết nối nghiêm túc.",
    cta: "Dùng thử Pro miễn phí",
    href: "/signup",
    features: ["Mọi thứ trong gói Miễn phí", "Thiết kế thẻ tùy chỉnh", "Phân tích nâng cao", "Tích hợp CRM", "Nhắc nhở theo dõi", "Hỗ trợ ưu tiên"],
    featured: true,
  },
  {
    name: "Đội nhóm",
    planKey: "teams" as const,
    desc: "Dành cho đội nhóm từ 5 người trở lên.",
    cta: "Dùng thử cho đội",
    href: "/signup",
    features: ["Mọi thứ trong gói Pro", "Bảng điều khiển & quản trị đội nhóm", "Tạo thẻ hàng loạt", "Kiểm soát thương hiệu & quyền", "SSO & tích hợp doanh nghiệp", "Quản lý tài khoản chuyên trách"],
  },
];

function IndexPage() {
  const { country } = useCurrency();
  const heroVideoRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroVideoRef,
    offset: ["start end", "end start"],
  });
  const heroScale = useTransform(heroScroll, [0, 0.45, 1], [0.94, 1.04, 1.08]);

  const supersectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: superScroll } = useScroll({
    target: supersectionRef,
    offset: ["start 85%", "center center"],
  });
  // Images fly outward as user scrolls; video grows. At progress 1 (midpoint
  // of section in viewport) images are fully out and video is full size.
  // Linger 10% longer before flying out — start outward motion at 0.1
  const floatLeftX = useTransform(superScroll, [0.1, 1], [0, -520]);
  const floatLeftY = useTransform(superScroll, [0.1, 1], [0, 140]);
  const floatLeftR = useTransform(superScroll, [0.1, 1], [-6, -18]);

  const floatRightX = useTransform(superScroll, [0.1, 1], [0, 520]);
  const floatRightY = useTransform(superScroll, [0.1, 1], [0, 120]);
  const floatRightR = useTransform(superScroll, [0.1, 1], [6, 18]);

  const floatBottomX = useTransform(superScroll, [0.1, 1], [0, -120]);
  const floatBottomY = useTransform(superScroll, [0.1, 1], [0, 360]);
  const floatBottomR = useTransform(superScroll, [0.1, 1], [-3, -14]);

  const floatOpacity = useTransform(superScroll, [0.1, 0.9, 1], [1, 0.2, 0]);
  const videoScale = useTransform(superScroll, [0, 1], [0.82, 1.02]);

  // Mute toggles for both videos
  const heroVideoEl = useRef<HTMLVideoElement>(null);
  const featuresVideoEl = useRef<HTMLVideoElement>(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const [featuresMuted, setFeaturesMuted] = useState(true);
  const toggleMute = (
    el: React.RefObject<HTMLVideoElement | null>,
    muted: boolean,
    setMuted: (v: boolean) => void,
  ) => {
    const next = !muted;
    setMuted(next);
    if (el.current) {
      el.current.muted = next;
      if (!next) {
        el.current.play().catch(() => {});
      }
    }
  };

  return (
    <SiteLayout>
      {/* SUPERCHARGED — white section with floating images around a video */}
      <section className="relative overflow-hidden bg-background">
        <div className="mx-auto max-w-[1400px] px-4 py-20 md:px-8 md:py-36">
          <div className="mx-auto max-w-3xl text-left md:text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              SIÊU ỨNG DỤNG KẾT NỐI
            </span>
            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.1] md:whitespace-nowrap md:text-5xl">
              Doanh nghiệp của bạn.{" "}
              <span className="relative inline-block align-baseline">
                <svg
                  className="sc-svg"
                  viewBox="0 0 740 100"
                  preserveAspectRatio="xMinYMid meet"
                  aria-hidden="true"
                  focusable="false"
                >
                  <defs>
                    <linearGradient id="sc-grad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#fbbf77" />
                      <stop offset="20%" stopColor="#f59e88" />
                      <stop offset="40%" stopColor="#f06c8a" />
                      <stop offset="55%" stopColor="#ec4899" />
                      <stop offset="72%" stopColor="#c563d6" />
                      <stop offset="88%" stopColor="#b057ee" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                  </defs>
                  <text x="0" y="80" className="sc-base">Tăng tốc.</text>
                  <text x="0" y="80" className="sc-fill" fill="url(#sc-grad)">Tăng tốc.</text>
                  <text x="0" y="80" className="sc-trail" stroke="url(#sc-grad)">Tăng tốc.</text>
                </svg>
                <span className="sr-only">Tăng tốc.</span>
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground md:mx-auto md:text-lg">
              banana.cards là các công cụ thông minh được hỗ trợ bởi AI, được thiết kế để giúp doanh nghiệp của bạn phát triển. Chia sẻ hồ sơ số chỉ bằng một cú chạm và mở khóa một hệ sinh thái theo dõi khách hàng tiềm năng, viết thư theo dõi và tự động hóa quy trình làm việc — thương hiệu, dữ liệu, doanh nghiệp của bạn, tất cả được kết nối.
            </p>
            <div className="mt-8 flex w-full md:w-auto md:justify-center">
              <CtaLink to="/signup" className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-dark px-7 py-3.5 text-sm font-bold text-background transition hover:opacity-90 md:inline-flex md:w-auto">
                Bắt đầu miễn phí <ArrowRight className="h-4 w-4" />
              </CtaLink>
            </div>
          </div>

          <div ref={supersectionRef} className="relative mx-auto mt-12 max-w-5xl md:mt-20">
            {/* Floating card scan — top-left */}
            <motion.img
              src={floatCardScan}
              alt="Máy quét danh thiếp"
              style={{ x: floatLeftX, y: floatLeftY, rotate: floatLeftR, opacity: floatOpacity }}
              className="pointer-events-none absolute -left-6 -top-16 z-20 hidden w-[230px] rounded-2xl md:block md:w-[300px] lg:-left-20 lg:w-[360px]"
              loading="lazy"
            />
            {/* Floating profile — right */}
            <motion.img
              src={floatProfile}
              alt="Hồ sơ danh thiếp số"
              style={{ x: floatRightX, y: floatRightY, rotate: floatRightR, opacity: floatOpacity }}
              className="pointer-events-none absolute -right-4 top-12 z-20 hidden w-[180px] rounded-3xl md:block md:w-[220px] lg:-right-16 lg:w-[260px]"
              loading="lazy"
            />
            {/* Floating data — bottom-left */}
            <motion.img
              src={floatData}
              alt="Bảng điều khiển phân tích"
              style={{ x: floatBottomX, y: floatBottomY, rotate: floatBottomR, opacity: floatOpacity }}
              className="pointer-events-none absolute -bottom-12 left-2 z-20 hidden w-[200px] rounded-2xl md:block md:w-[240px] lg:-left-12 lg:-bottom-20 lg:w-[280px]"
              loading="lazy"
            />

            {/* Video — grows as user scrolls */}
            <motion.div
              style={{ scale: videoScale }}
              className="relative origin-center overflow-hidden rounded-2xl bg-brand-dark"
            >
              <video ref={featuresVideoEl} className="aspect-video w-full object-cover rounded-none opacity-100" autoPlay loop muted playsInline poster={heroCard} preload="none">
                <source src="/features-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(featuresVideoEl, featuresMuted, setFeaturesMuted)}
                aria-label={featuresMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                className="absolute bottom-4 right-4 z-30 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {featuresMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOGO MARQUEE — Được tin dùng bởi các chuyên gia tại */}
      <section className="bg-white">
        <div className="relative pb-20 pt-8 md:pb-28 md:pt-10 border-[#fcfcfc]">
          <p className="mb-5 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:mb-6">
            Được tin dùng bởi các chuyên gia tại
          </p>
          <div className="relative overflow-hidden">
            <div className="animate-marquee flex w-max gap-3 px-6 [mask-image:linear-gradient(to_right,transparent,#000_10%,#000_90%,transparent)] md:gap-4">
              {[...LOGOS, ...LOGOS].map((name, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-5 py-3 text-base font-bold tracking-tight text-foreground/60 md:px-7 md:py-4 md:text-xl"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APPS SHOWCASE — bento grid of platform apps */}
      <section className="relative overflow-hidden bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <div className="mb-12 flex flex-col items-start gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-card px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
                CRM HỖ TRỢ BỞI AI
              </span>
              <h2 className="mt-5 font-display text-3xl leading-[1.05] tracking-[-0.01em] md:text-5xl">
                Các ứng dụng <em className="italic">vận hành doanh nghiệp.</em>
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-muted-foreground md:text-lg">
                Mỗi tài khoản banana.cards đi kèm với bộ ứng dụng gốc ngày càng phát triển — chỉ cài đặt những gì bạn cần, tất cả kết nối với liên hệ và AI của bạn.
              </p>
            </div>
            <Link
              to="/platform"
              className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90"
            >
              Xem tất cả ứng dụng <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CalendarCheck, name: "Đặt lịch", tag: "Lịch hẹn", desc: "Cho phép khách hàng tiềm năng đặt cuộc họp ngay khi họ chạm thẻ. Đồng bộ lịch, nhắc nhở, thanh toán — tất cả tích hợp sẵn.", href: "/platform/bookings", color: "from-[#E8F5D8] to-[#C9E89A]" },
              { icon: FileSignature, name: "Hợp đồng", tag: "Ký điện tử", desc: "Gửi, ký và lưu trữ hợp đồng mà không rời khỏi ứng dụng. Có giá trị pháp lý, sẵn sàng kiểm toán.", href: "/platform/contracts", color: "from-[#FFE9D6] to-[#FFC79A]" },
              { icon: Video, name: "Cuộc họp", tag: "Gọi trực tiếp", desc: "Cuộc họp video thông minh với ghi chú AI, hạng mục hành động và đồng bộ CRM tích hợp sẵn.", href: "/platform/meetings", color: "from-[#D0EEFF] to-[#8FCEFF]" },
              { icon: Users, name: "CRM", tag: "Liên hệ", desc: "Mọi cú chạm, quét và khách hàng tiềm năng đều đến đây — được gắn thẻ, chấm điểm và sẵn sàng hành động.", href: "/platform/crm", color: "from-[#D6F3EE] to-[#9CDCD0]" },
              { icon: KanbanSquare, name: "Pipeline", tag: "Vận hành bán hàng", desc: "Pipeline giao dịch trực quan tự động di chuyển khi liên hệ của bạn tương tác.", href: "/platform/pipelines", color: "from-[#E4E4E7] to-[#B8B8C0]" },
              { icon: ListChecks, name: "Công việc", tag: "Quy trình", desc: "Biến cuộc trò chuyện thành công việc. Giao, theo dõi và đóng — cá nhân hoặc theo nhóm.", href: "/platform/tasks", color: "from-[#FFF4C2] to-[#FFE066]" },
              { icon: Radar, name: "Scout", tag: "Tìm kiếm bằng AI", desc: "Để AI tìm khách hàng giống khách hàng tốt nhất của bạn và bắt đầu tiếp cận cho bạn.", href: "/platform/scout", color: "from-[#DCE7FF] to-[#A8C0FF]" },
              { icon: Sparkles, name: "Bộ AI", tag: "Trợ lý AI", desc: "Theo dõi, tóm tắt, làm giàu dữ liệu và trả lời — viết cho bạn bằng giọng văn của bạn.", href: "/platform/ai-suite", color: "from-[#EADFFF] to-[#C7AEFF]" },
            ].map((app, i) => {
              const Icon = app.icon;
              const featured = false;
              return (
                <Link
                  key={app.name}
                  to={app.href}
                  className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl bg-card p-6 transition hover:-translate-y-1 ${
                    featured ? "lg:col-span-2 lg:row-span-1" : ""
                  }`}
                >
                  {/* Placeholder image area with gradient */}
                  <div className={`relative mb-5 flex h-32 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${app.color}`}>
                    <Icon className="h-12 w-12 text-foreground/70 transition group-hover:scale-110" strokeWidth={1.5} />
                    <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-background/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-foreground backdrop-blur">
                      {app.tag}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-xl leading-tight md:text-2xl">{app.name}</h3>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted transition group-hover:bg-foreground group-hover:text-background">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{app.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] font-medium uppercase tracking-[0.2em] text-muted-foreground md:mt-14">
            <span>+ Tích hợp</span><span className="text-foreground/20">·</span>
            <span>+ Phân tích</span><span className="text-foreground/20">·</span>
            <span>+ Wallet</span><span className="text-foreground/20">·</span>
            <span>+ Trao đổi khách hàng tiềm năng</span><span className="text-foreground/20">·</span>
            <span>ứng dụng mới mỗi tháng</span>
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Dành cho tất cả mọi người</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Bạn là ai. <em className="italic">Bạn làm việc thế nào.</em>
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PERSONAS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[5/4] overflow-hidden bg-muted">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-10 rounded-3xl bg-card p-7 md:mt-12 md:p-14">
          <div className="grid items-center gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="font-display text-2xl leading-tight md:text-4xl">
                Thiết kế cho đội nhóm.
              </h3>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
                Từ 5 đến 5.000 nhân viên — quản lý mọi thẻ, thương hiệu và kết nối từ một bảng điều khiển quản trị. Được xây dựng cho doanh nghiệp.
              </p>
            </div>
            <div className="md:text-right">
              <Link to="/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Khám phá gói đội nhóm <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* PLATFORM — left sticky intro, right scrolling features */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 md:px-6 md:pb-28 md:pt-24">
          <div className="grid gap-10 md:grid-cols-12 md:gap-12">
            {/* Sticky intro — left column on desktop */}
            <aside className="md:col-span-5 sticky top-24 self-start">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Nền tảng</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.05] md:text-5xl">
                Doanh nghiệp của bạn. <em className="italic">Tăng tốc.</em>
              </h2>
              <p className="mt-5 max-w-md text-[15px] text-muted-foreground md:text-base">
                banana.cards không chỉ là một chiếc thẻ — đó là danh tính số đầy đủ và trung tâm kết nối của bạn. Được xây dựng cho cá nhân, đội nhóm và doanh nghiệp.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                  Bắt đầu miễn phí <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:opacity-80">
                  Xem bảng giá
                </Link>
              </div>
              {/* Decorative count chip */}
              <p className="mt-10 hidden text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:block">
                <span className="font-display text-2xl text-foreground">{PLATFORM_FEATURES.length}</span>
                <span className="ml-2 align-middle">tính năng · một nền tảng</span>
              </p>
            </aside>

            {/* Feature list — right column */}
            <div className="md:col-span-7">
              <ol className="space-y-5 md:space-y-6">
                {PLATFORM_FEATURES.map((f, i) => {
                  const total = PLATFORM_FEATURES.length;
                  const num = `0${i + 1}`;
                  return (
                    <li key={f.eyebrow} className="rounded-3xl bg-card p-6 md:p-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className="inline-flex items-center gap-2 rounded-xl bg-foreground px-3 py-1.5 font-display text-sm font-bold tracking-wider text-background">
                          <span className="text-brand-lime">{num}</span>
                          <span className="text-background/60">/ 0{total}</span>
                        </span>
                        <Link to={f.href} className="inline-flex items-center gap-1 text-xs font-bold underline-offset-4 hover:underline">
                          {f.cta} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                      <h3 className="mt-5 font-display text-2xl leading-[1.15] md:text-3xl">
                        {f.eyebrow} <em className="italic">{f.italic}</em>
                      </h3>
                      <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-muted-foreground md:text-[15px]">
                        {f.desc}
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {f.chips.map((c) => (
                          <span key={c} className="inline-flex items-center rounded-full bg-muted px-3 py-1.5 text-[12px] font-semibold border">
                            {c}
                          </span>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* STATS — matches section above (muted), white box inside */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
          <div className="grid grid-cols-2 gap-6 rounded-3xl bg-background p-8 md:grid-cols-4 md:gap-8 md:p-12">
            {STATS.map((s) => (
              <div key={s.l}>
                <p className="font-display text-4xl leading-none md:text-6xl">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HERO — Change the way you connect */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-white md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-[1600px] px-4 pb-14 pt-10 md:px-8 md:pb-20 md:pt-24 2xl:px-12">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              Tương lai của kết nối
            </span>
            <h1 className="mt-5 font-display text-4xl leading-[1.05] tracking-[-0.02em] md:text-6xl md:leading-[1.02]">
              Thay đổi cách bạn kết nối.
            </h1>
            <p className="mt-5 max-w-2xl text-[15px] text-muted-foreground md:text-lg">
              Hơn cả một danh thiếp số. Một nền tảng kết nối hoàn chỉnh — NFC, thẻ ảo, liên hệ được hỗ trợ bởi AI và quản lý đội nhóm. Tất cả trong một.
            </p>
            <div className="mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Bắt đầu miễn phí <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/shop" className="inline-flex items-center justify-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80">
                Đặt thẻ NFC
              </Link>
            </div>
            <p className="mt-5 text-[12px] text-muted-foreground">
              Không cần thẻ tín dụng · Hoạt động trên iPhone & Android · Được yêu thích bởi hơn 50.000 chuyên gia
            </p>
          </div>

          <motion.div ref={heroVideoRef} style={{ scale: heroScale }} className="relative mt-10 origin-center md:mt-20">
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl bg-brand-dark">
              <video
                ref={heroVideoEl}
                className="aspect-video w-full object-cover rounded-none opacity-100"
                autoPlay
                loop
                muted
                playsInline
                poster={heroCard}
                preload="metadata"
                // @ts-expect-error fetchpriority is valid HTML but missing from React types
                fetchpriority="high"
              >
                <source src="/hero-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(heroVideoEl, heroMuted, setHeroMuted)}
                aria-label={heroMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                className="absolute bottom-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {heroMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* CARD SPECS */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
            <img src={nfcThin} alt="Cận cảnh chip NFC" loading="lazy" width={1600} height={1280} className="relative w-full rounded-2xl object-cover" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Xây dựng để bền</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Mỏng đến khó tin. <em className="italic">Mạnh mẽ đến kinh ngạc.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-muted-foreground md:text-base">
              Thẻ NFC của chúng tôi mỏng hơn thẻ tín dụng, bền bỉ và nhúng toàn bộ danh tính số chỉ với một cú chạm.
            </p>
            <dl className="mt-7 grid grid-cols-2 gap-x-5 gap-y-4 md:mt-8 md:gap-x-6 md:gap-y-5">
              {SPECS.map((s) => (
                <div key={s.l} className="rounded-xl bg-card p-4">
                  <dt className="font-display text-lg leading-none">{s.v}</dt>
                  <dd className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>


      {/* PRODUCTS — One platform. Every way to connect. */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-10 md:px-6 md:pb-24 md:pt-14">
        <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Sản phẩm của chúng tôi</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Một nền tảng.<br />Mọi cách để kết nối.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] text-muted-foreground md:text-base">
              Dù bạn chạm, quét hay chia sẻ liên kết — banana.cards hoạt động ở mọi nơi bạn gặp gỡ mọi người.
            </p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            Đặt & tùy chỉnh trực tuyến <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PRODUCTS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-muted transition">
              <div className="aspect-[5/4] overflow-hidden bg-card">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-7">
                <h3 className="font-display text-xl leading-tight md:text-3xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2 text-sm">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{b}</span></li>
                  ))}
                </ul>
                <span className="mt-7 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* CARD VARIANTS — Your business card. Your way. */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Đặt hàng trực tuyến</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
                Danh thiếp của bạn. <em className="italic">Theo cách của bạn.</em>
              </h2>
              <p className="mt-4 max-w-xl text-[15px] text-background/70 md:text-base">
                Thiết kế, đặt hàng và nhận thẻ NFC và thẻ số cao cấp — được tùy chỉnh hoàn toàn theo thương hiệu của bạn.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {CARD_VARIANTS.map((c) => (
              <div key={c.name} className="group flex flex-col overflow-hidden rounded-2xl bg-white/[0.06] transition hover:bg-white/[0.07]">
                <div className="aspect-[5/4] overflow-hidden bg-muted">
                  <img src={c.img} alt={c.name} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{c.name}</h3>
                  <p className="mt-2 text-sm text-background/65">{c.desc}</p>
                  <div className="mt-5 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
                    <p className="font-display text-2xl leading-none">
                      Từ {formatMoney(getProductBasePrice(c.slug, country.code), country)}
                    </p>
                    <Link to={c.href} className="inline-flex items-center gap-1.5 rounded-xl bg-brand-lime px-4 py-2 text-xs font-bold text-brand-lime-foreground transition hover:opacity-90">
                      Tùy chỉnh <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-white/5">
              Xem tất cả sản phẩm <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STEPS — From zero to connected in 3 steps */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Bắt đầu trong vài giây</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Từ con số 0 đến kết nối <em className="italic">trong 3 bước.</em>
          </h2>
        </div>
        <ol className="grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative flex flex-col rounded-2xl bg-muted p-6 md:p-8">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-foreground font-display text-xl font-bold text-background md:h-16 md:w-16 md:text-2xl">
                0{i + 1}
              </span>
              <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.tag}</p>
              <h3 className="mt-2 font-display text-2xl leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 flex justify-center">
          <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
            Bắt đầu miễn phí <ArrowRight className="h-4 w-4" />
          </CtaLink>
        </div>
      </section>

      {/* SUSTAINABILITY */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Bền vững</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Tốt cho bạn. <br /> <em className="italic">Và cho hành tinh.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-background/70 md:text-base">
              Mỗi thẻ NFC banana.cards thay thế hàng trăm thẻ giấy trong suốt vòng đời. Thẻ của chúng tôi được làm từ vật liệu tái chế hoặc có nguồn gốc bền vững. Ngừng in lại. Ngừng lãng phí.
            </p>
            <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 md:mt-10 md:gap-6 md:pt-8">
              {[
                { v: "500+", l: "thẻ giấy tiết kiệm mỗi thẻ NFC" },
                { v: "100%", l: "vận chuyển trung hòa carbon" },
                { v: "Xanh",  l: "tùy chọn PVC tái chế & tre" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl leading-none md:text-4xl">{s.v}</dt>
                  <dd className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-background/55 md:text-[11px]">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative">
            <BeforeAfterSlider
              beforeSrc="/lovable-uploads/six-before.webp"
              afterSrc="/lovable-uploads/six-after.webp"
              beforeAlt="Danh thiếp giấy cũ"
              afterAlt="Thẻ NFC banana.cards với hồ sơ số"
              beforeLabel="Giấy"
              afterLabel="banana.cards"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <ReviewsSection />

      {/* PRICING TEASER */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 max-w-2xl md:mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Bảng giá</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Giá đơn giản, minh bạch. <em className="italic">Bắt đầu miễn phí.</em>
            </h2>
            <p className="mt-4 text-[15px] text-muted-foreground md:text-base">
              Không phí ẩn. Không cam kết dài hạn. Bắt đầu miễn phí và mở rộng khi bạn phát triển.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {PLANS.map((p) => {
              const isFree = p.planKey === "free";
              const paidKey = p.planKey === "pro_ai" || p.planKey === "teams" ? p.planKey : null;
              const priceText = isFree
                ? `${country.symbol}0`
                : paidKey
                  ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, "month"))
                  : defaultPlanPriceText("pro_ai", "month");
              const perLabel = p.planKey === "teams" ? "/ chỗ / tháng" : "/ tháng";
              return (
              <div key={p.name} className={`relative flex flex-col rounded-2xl bg-card p-7 md:p-8 ${p.featured ? "ring-2 ring-foreground" : ""}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-6 inline-flex h-6 items-center justify-center rounded-xl bg-brand-lime px-3 text-[11px] font-bold text-brand-lime-foreground">
                    Phổ biến nhất
                  </span>
                )}
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{p.name}</p>
                <div className="mt-4 flex items-end gap-1.5">
                  <p className="font-display text-4xl leading-none md:text-5xl">{priceText}</p>
                  <p className="pb-1 text-sm text-muted-foreground">{perLabel}</p>
                </div>
                {!isFree && (
                  <span className="mt-1 inline-flex w-fit items-center rounded-full bg-brand-lime px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
                    GIẢM 50% NĂM
                  </span>
                )}
                <p className="mt-3 text-sm text-muted-foreground">{p.desc}</p>
                <CtaLink to={p.href} className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition ${p.featured ? "bg-brand-dark text-background hover:opacity-90" : "bg-muted hover:bg-muted/70"}`}>
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <ul className="mt-8 grid gap-2.5 border-t border-foreground/10 pt-6 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{f}</span></li>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Tất cả gói đều có 14 ngày dùng thử miễn phí. Không cần thẻ tín dụng.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* BLOGS / RESOURCES */}
      <BlogsSection />

      {/* FINAL CTA — stacks onto the blogs section above */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark p-8 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_55%)]" aria-hidden />
          <img src={logoSvg} alt="" aria-hidden width={115} height={20} className="mx-auto h-5 w-auto brightness-0 invert" />
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
            Bắt đầu kết nối <em className="italic text-brand-lime">khác biệt.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[15px] text-background/70 md:text-base">
            Tham gia cùng hơn 50.000 chuyên gia đã nâng cấp cách họ kết nối. Mất 2 phút để thiết lập.
          </p>
          <div className="relative mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
            <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
              Bắt đầu — miễn phí
            </CtaLink>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
              Trao đổi với bộ phận bán hàng
            </Link>
          </div>
        </div>
        </div>
      </section>

    </SiteLayout>
  );
}

function ReviewCard({ q, a, r }: { q: string; a: string; r: string }) {
  return (
    <figure className="flex flex-col rounded-2xl bg-muted p-5 md:p-6">
      <div className="flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-foreground text-foreground" />
        ))}
      </div>
      <blockquote className="mt-3 text-[14px] leading-relaxed">"{q}"</blockquote>
      <figcaption className="mt-5 text-xs">
        <span className="font-bold">{a}</span> <span className="text-muted-foreground">— {r}</span>
      </figcaption>
    </figure>
  );
}

function ReviewsSection() {
  const all = [...REVIEWS, ...REVIEWS_EXTRA];
  const cols = [
    all.filter((_, i) => i % 3 === 0),
    all.filter((_, i) => i % 3 === 1),
    all.filter((_, i) => i % 3 === 2),
  ];
  const animClasses = ["animate-marquee-y", "animate-marquee-y-rev", "animate-marquee-y-slow"];
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 flex flex-col items-center gap-3 text-center md:mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Mọi người nói gì</p>
        <h2 className="font-display text-3xl leading-[1.1] md:text-5xl">
          Được yêu thích bởi hơn 50.000 chuyên gia <em className="italic">tại 170 quốc gia.</em>
        </h2>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-foreground text-foreground" />
            ))}
          </div>
          <p className="text-sm font-bold">4.9 / 5 · 1.200+ đánh giá</p>
        </div>
      </div>
      <div
        className="relative grid h-[640px] grid-cols-1 gap-4 overflow-hidden md:grid-cols-3 md:gap-5"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
        }}
      >
        {cols.map((col, idx) => (
          <div key={idx} className={`flex flex-col gap-4 md:gap-5 ${animClasses[idx]}`}>
            {[...col, ...col].map((t, i) => (
              <ReviewCard key={`${idx}-${i}`} {...t} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-foreground/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-lg md:text-xl">{q}</span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && <p className="pb-5 pr-12 text-[15px] leading-relaxed text-muted-foreground">{a}</p>}
    </div>
  );
}

function FAQSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-5xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 text-center md:mb-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Câu hỏi thường gặp</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
          Có câu hỏi? <em className="italic">Chúng tôi có câu trả lời.</em>
        </h2>
      </div>
      <div className="mx-auto max-w-3xl rounded-3xl bg-muted px-6 md:px-10">
        {FAQS.map((f) => (
          <FAQItem key={f.q} {...f} />
        ))}
      </div>
      </div>
    </section>
  );
}

function BlogsSection() {
  return (
    <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 text-center md:mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tài nguyên</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Blog, tài nguyên <em className="italic">và hướng dẫn.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] text-muted-foreground md:text-base">
            Câu trả lời nhanh cho những câu hỏi phổ biến nhất về danh thiếp số và kết nối hiện đại.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {BLOGS.map((b) => (
            <Link key={b.title} to={b.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img src={b.img} alt={b.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.03]" />
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-lg leading-tight md:text-xl">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  Đọc bài viết <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
